import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Invoice, Organization, OrganizationListItem } from '@repo/api-client/dtos';
import { TEMPLATE_MANIFEST, type TemplateId } from '@repo/api-client';
import { useDebouncedUpdateOrganization, useUpdateInvoiceMutation } from '@repo/queries';

import { isPreviewInvoiceId } from '@/features/invoice/buildPreviewInvoice';

/**
 * Customize-toolbar plumbing for the live invoice preview.
 *
 * Template selection is **per-invoice** (`invoice.templateId`) — the
 * same field the printer and frozen PDF use. Org `branding.template` is
 * only a fallback for unsaved form previews (ghost invoice id).
 *
 * Accent / logo / line-item visibility toggles still patch org branding
 * (org-wide defaults), matching mobile.
 */
const SAVE_DEBOUNCE_MS = 600;

type BrandingPatch = {
  accent?: string;
  primaryColor?: string;
  showLogo?: boolean;
  showFooter?: boolean;
  showItemDiscount?: boolean;
  showItemTax?: boolean;
  showItemNotes?: boolean;
  /** Org-default template — only used when there is no persisted invoice. */
  template?: TemplateId;
};

export interface UseCustomizeArgs {
  organization: Organization | OrganizationListItem | null;
  /** When set (and not a ghost preview id), template picks PATCH this invoice. */
  invoice?: Pick<Invoice, 'id' | 'templateId'> | null;
}

export interface CustomizeState {
  effectiveOrg: Organization | OrganizationListItem | null;
  effectiveTemplateId: TemplateId;
  effectiveAccent: string;
  effectiveShowLogo: boolean;
  effectiveShowFooter: boolean;
  effectiveShowItemDiscount: boolean;
  effectiveShowItemTax: boolean;
  effectiveShowItemNotes: boolean;
  setTemplate: (id: TemplateId) => void;
  setAccent: (color: string) => void;
  setShowLogo: (next: boolean) => void;
  setShowFooter: (next: boolean) => void;
  setShowItemDiscount: (next: boolean) => void;
  setShowItemTax: (next: boolean) => void;
  setShowItemNotes: (next: boolean) => void;
  saving: boolean;
}

export function useCustomize({
  organization,
  invoice,
}: UseCustomizeArgs): CustomizeState {
  const { call: debouncedUpdate, pending: orgSaving } =
    useDebouncedUpdateOrganization(SAVE_DEBOUNCE_MS);
  const updateInvoiceMutation = useUpdateInvoiceMutation();
  const [patch, setPatch] = useState<BrandingPatch>({});
  const [pendingTemplateId, setPendingTemplateId] = useState<TemplateId | null>(null);
  const [templateSaving, setTemplateSaving] = useState(false);

  const persistTemplateToInvoice =
    Boolean(organization?.id && invoice?.id && !isPreviewInvoiceId(invoice.id));

  const orgIdRef = useRef<string | null>(null);
  const invoiceIdRef = useRef<string | null>(null);
  useEffect(() => {
    const orgId = organization?.id ?? null;
    const invId = invoice?.id ?? null;
    if (orgIdRef.current !== orgId || invoiceIdRef.current !== invId) {
      orgIdRef.current = orgId;
      invoiceIdRef.current = invId;
      setPatch({});
      setPendingTemplateId(null);
    }
  }, [organization?.id, invoice?.id]);

  const persistedBranding = (organization?.branding ?? null) as
    | Record<string, unknown>
    | null;

  const effectiveBranding = useMemo<Record<string, unknown>>(() => {
    return { ...(persistedBranding ?? {}), ...patch };
  }, [persistedBranding, patch]);

  const effectiveOrg = useMemo(() => {
    if (!organization) return null;
    return { ...organization, branding: effectiveBranding } as
      | Organization
      | OrganizationListItem;
  }, [organization, effectiveBranding]);

  const persistedTemplateId = normaliseTemplateId(invoice?.templateId);
  const effectiveTemplateId =
    pendingTemplateId ??
    persistedTemplateId ??
    readTemplateIdFromBranding(effectiveBranding);

  const effectiveAccent = readString(effectiveBranding, 'accent') ?? '#3D466A';
  const effectiveShowLogo = readBool(effectiveBranding, 'showLogo', true);
  const effectiveShowFooter = readBool(effectiveBranding, 'showFooter', true);
  const effectiveShowItemDiscount = readBool(effectiveBranding, 'showItemDiscount', true);
  const effectiveShowItemTax = readBool(effectiveBranding, 'showItemTax', true);
  const effectiveShowItemNotes = readBool(effectiveBranding, 'showItemNotes', true);

  const commit = useCallback(
    (next: BrandingPatch) => {
      if (!organization) return;
      debouncedUpdate({
        organizationId: organization.id,
        updates: {
          branding: { ...(persistedBranding ?? {}), ...patch, ...next },
        },
      });
    },
    [organization, persistedBranding, patch, debouncedUpdate],
  );

  const applyBranding = useCallback(
    (next: BrandingPatch) => {
      setPatch((prev) => ({ ...prev, ...next }));
      commit(next);
    },
    [commit],
  );

  const setTemplate = useCallback(
    (id: TemplateId) => {
      if (id === effectiveTemplateId) return;

      if (persistTemplateToInvoice && organization && invoice?.id) {
        setPendingTemplateId(id);
        setTemplateSaving(true);
        void updateInvoiceMutation
          .mutateAsync({
            organizationId: organization.id,
            id: invoice.id,
            templateId: id,
          })
          .finally(() => {
            setTemplateSaving(false);
            setPendingTemplateId(null);
          });
        return;
      }

      applyBranding({ template: id });
    },
    [
      effectiveTemplateId,
      persistTemplateToInvoice,
      organization,
      invoice?.id,
      updateInvoiceMutation,
      applyBranding,
    ],
  );

  return {
    effectiveOrg,
    effectiveTemplateId,
    effectiveAccent,
    effectiveShowLogo,
    effectiveShowFooter,
    effectiveShowItemDiscount,
    effectiveShowItemTax,
    effectiveShowItemNotes,
    setTemplate,
    setAccent: (color) => applyBranding({ accent: color, primaryColor: color }),
    setShowLogo: (v) => applyBranding({ showLogo: v }),
    setShowFooter: (v) => applyBranding({ showFooter: v }),
    setShowItemDiscount: (v) => applyBranding({ showItemDiscount: v }),
    setShowItemTax: (v) => applyBranding({ showItemTax: v }),
    setShowItemNotes: (v) => applyBranding({ showItemNotes: v }),
    saving: orgSaving || templateSaving,
  };
}

function readString(b: Record<string, unknown> | null, key: string): string | undefined {
  const v = b?.[key];
  return typeof v === 'string' ? v : undefined;
}

function readBool(
  b: Record<string, unknown> | null,
  key: string,
  fallback: boolean,
): boolean {
  const v = b?.[key];
  return typeof v === 'boolean' ? v : fallback;
}

function normaliseTemplateId(raw: string | undefined | null): TemplateId {
  if (raw && TEMPLATE_MANIFEST.some((t) => t.id === raw)) {
    return raw as TemplateId;
  }
  return TEMPLATE_MANIFEST[0]!.id;
}

function readTemplateIdFromBranding(b: Record<string, unknown> | null): TemplateId {
  return normaliseTemplateId(readString(b, 'template'));
}
